import Link from 'next/link'

import { ReceiptStatus, TaxReceiptType } from '@/entities/user'
import {
  formatTaxInvoiceNumber,
  ReceiptInformationUpdate,
  TaxReceiptTypeMap,
  useGuestReceiptInformation,
  useReceiptInformationValidation,
} from '@/features/update-receipt-information'
import { formatPhoneNumber } from '@/shared/lib'
import { Button } from '@/shared/ui'

type Props = {
  contractId: number
}

export const ReceiptInformation = ({ contractId }: Props) => {
  const { receiptInformation, hasReceiptInformation } =
    useGuestReceiptInformation({ contractId })

  const { handleReceiptFormOpen } = useReceiptInformationValidation()

  const receiptRequested = receiptInformation?.receiptRequested
  const isFailed = receiptInformation?.status === ReceiptStatus.FAILED
  const isCompleted = receiptInformation?.status === ReceiptStatus.COMPLETED

  const formatIssuanceNumber = (issuanceNumber: string) => {
    if (receiptInformation?.taxReceiptType === TaxReceiptType.TAX_INVOICE) {
      return formatTaxInvoiceNumber(issuanceNumber)
    }

    return formatPhoneNumber(issuanceNumber)
  }

  return (
    <div className="space-y-3">
      <h3 className="typo-body1 font-semibold text-black">영수증 발급</h3>

      {(() => {
        if (isFailed) {
          return (
            <div className="space-y-2">
              <p className="typo-label1 font-medium text-gray-900">발급 실패</p>
              <p className="typo-label2 mb-3 font-normal whitespace-break-spaces text-gray-500">
                {receiptInformation.failedReason}
              </p>
            </div>
          )
        }

        if (isCompleted && receiptRequested) {
          const taxReceiptType =
            TaxReceiptTypeMap[
              receiptInformation.taxReceiptType as TaxReceiptType
            ]

          return (
            <div className="space-y-4">
              <p className="typo-label1 font-medium text-gray-900">
                신청 ({taxReceiptType}{' '}
                {formatIssuanceNumber(receiptInformation.issuanceNumber!)})
              </p>
              {receiptInformation.billUrl && (
                <Button
                  variant="outline"
                  size="medium"
                  shape="pill"
                  fullWidth
                  asChild
                >
                  <Link
                    href={receiptInformation.billUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {taxReceiptType}
                  </Link>
                </Button>
              )}
            </div>
          )
        }

        if (isCompleted && !receiptRequested) {
          return (
            <div>
              <p className="typo-label1 mb-2 font-medium text-gray-900">
                신청 안 함
              </p>
              <p className="typo-label2 mb-4 font-normal whitespace-break-spaces text-gray-500">
                영수증 미신청 또는 입력 정보 오류로 영수증이 자진발급되었습니다.
                <br />
                현금영수증 등록이 필요한 경우, 홈택스에서 국세청 승인번호로
                등록해 주세요.
              </p>

              <Button
                variant="outline"
                size="medium"
                shape="pill"
                fullWidth
                asChild
              >
                <Link
                  href={receiptInformation.billUrl ?? ''}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  현금영수증(자진발급)
                </Link>
              </Button>
            </div>
          )
        }

        return (
          <ReceiptInformationUpdate
            defaultFormOpen={!hasReceiptInformation(receiptInformation)}
            hasDefaultReceiptInformation={hasReceiptInformation(
              receiptInformation,
            )}
            onFormOpen={handleReceiptFormOpen}
          />
        )
      })()}
    </div>
  )
}
